import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { ChangePasswordRequest, ChangePasswordResponse } from '@nadoutong/contracts';
import {
  changePassword as apiChangePassword,
  fetchSession,
  requestSetPasswordCode as apiRequestSetPasswordCode,
  setPassword as apiSetPassword,
} from '../api/auth.ts';
import { PlatformApiError } from '../api/client.ts';
import { localizeApiError } from '../api/errorMessages.ts';
import { StatusChip } from '../components/StatusChip.tsx';
import { TechnicalDetails } from '../components/TechnicalDetails.tsx';
import { useToast } from '../components/Toast.tsx';
import { hasPermission, sessionUserFromResponse, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, FormField, Input, PageHeader, Panel } from '../ui/index.ts';
import { localizeAuthError } from './auth/authErrors.ts';
import type { AuthErrorKeys } from './auth/authErrors.ts';
import { MembersCard } from './settings/MembersCard.tsx';

/**
 * `/settings` — account info, technical ids, org members, and the
 * in-session password change (epic #950 PR-4, local Field Notes
 * rebuild of the console-shared `createSettingsPage` factory
 * instantiation: sections account → technical → members → security,
 * members shown when the caller holds `org:members:read`).
 *
 * Password change calls `POST /v1/auth/password/change`; a 401
 * INVALID_CREDENTIALS envelope renders as an inline "current password
 * incorrect" field error. INVALID_PAYLOAD uses the localized payload
 * copy on the new-password field, never the server's English sentence.
 * Length is validated locally and on the
 * server (10–128 chars). Invite-activated accounts have no password
 * (`hasPassword === false`); the card then runs the in-place set-
 * password flow (`/v1/auth/password/set/code` then `/set`) instead of
 * asking for a current password. Missing `hasPassword` on the wire
 * (router not yet promoted) keeps the change form.
 *
 * The `/settings/{account,security,technical}` sub-paths are STATIC
 * routes, not one dynamic `:section` — an unknown section is then a
 * plain 404 through `App.tsx`'s catch-all rather than a silent
 * fallback to the top of this page, so the component needs no
 * validation branch. Each section carries `id="settings-section-<name>"`
 * and mount scrolls the requested one into view.
 */

const MIN_PASSWORD_LENGTH = 10;
const MAX_PASSWORD_LENGTH = 128;
const SET_PASSWORD_CODE_PATTERN = /^[0-9]{6}$/;

/**
 * Client resend cooldown after a successful send, aligned with the
 * server's 1/minute limit. Issue #137 originally said "no client
 * countdown" — the user overrode that on 2026-08-29 after using the
 * panel. The server still denies out-of-sync clicks with RATE_LIMITED,
 * which still renders as an inline error under the code+send row.
 */
const SEND_CODE_COOLDOWN_SECONDS = 60;

const SET_PASSWORD_SEND_ERROR_KEYS: AuthErrorKeys = {
  RATE_LIMITED: 'settings.security.set_password.error.send_rate_limited',
};

export type SettingsSection = 'account' | 'security' | 'technical';

export interface SettingsProps {
  readonly initialSection?: SettingsSection;
}

export function Settings({ initialSection }: SettingsProps): JSX.Element {
  const { user } = useSession();
  const t = useT();
  const showMembers = hasPermission(user, 'org:members:read');

  useEffect(() => {
    // Plain `/settings` asked for no section, so it must not scroll at
    // all. Defaulting to `account` would look identical on a fresh load
    // and then diverge the moment the page is entered scrolled — a Back
    // navigation restoring position, say — by yanking the view to the
    // top for no reason the user can see.
    if (initialSection === undefined) {
      return;
    }
    const target = document.getElementById(`settings-section-${initialSection}`);
    target?.scrollIntoView({ block: 'start' });
  }, [initialSection]);

  return (
    <section data-testid="settings-page" className="flex flex-col gap-4">
      {/*
        The heading promises what the page delivers, and for a reader
        without `org:members:read` that is the account half only — the
        team half is the card below, which they do not get.
      */}
      <PageHeader
        title={t(showMembers ? 'settings.title' : 'settings.title_personal')}
        sub={t(showMembers ? 'settings.subtitle' : 'settings.subtitle_personal')}
        testId="settings-header"
      />
      <div id="settings-section-account" className="scroll-mt-8">
        <AccountCard />
      </div>
      <div id="settings-section-technical" className="scroll-mt-8">
        <TechnicalCard />
      </div>
      {showMembers ? <MembersCard /> : null}
      <div id="settings-section-security" className="scroll-mt-8">
        <ChangePasswordCard />
      </div>
    </section>
  );
}

function AccountCard(): JSX.Element {
  const { user } = useSession();
  const t = useT();
  return (
    <Panel caption={t('settings.account.title')} padded testId="settings-account-card">
      <div className="flex flex-col gap-4">
        <p className="m-0 text-caption text-ink-low">{t('settings.account.description')}</p>
        <dl className="m-0 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <dt className="text-label text-ink-mid">{t('settings.account.email')}</dt>
            <dd className="m-0 font-mono text-body text-ink" data-testid="settings-account-email">
              {user?.email ?? '—'}
            </dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-label text-ink-mid">{t('settings.account.email_verified')}</dt>
            <dd className="m-0" data-testid="settings-email-verified">
              {user?.emailVerified === true ? (
                <StatusChip variant="ok" label={t('settings.account.status.verified')} />
              ) : (
                <StatusChip variant="warning" label={t('settings.account.status.unverified')} />
              )}
            </dd>
          </div>
        </dl>
      </div>
    </Panel>
  );
}

function TechnicalCard(): JSX.Element {
  const { user } = useSession();
  const t = useT();
  return (
    <TechnicalDetails summary={t('settings.technical.summary')} testId="settings-technical-card">
      <dl className="m-0 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <dt className="text-label text-ink-mid">{t('settings.account.account_id')}</dt>
          <dd className="m-0 font-mono text-body text-ink-mid" data-testid="settings-account-id">
            {user?.accountId ?? '—'}
          </dd>
        </div>
        <div className="flex flex-col gap-1">
          <dt className="text-label text-ink-mid">{t('settings.account.org_id')}</dt>
          <dd className="m-0 font-mono text-body text-ink-mid" data-testid="settings-org-id">
            {user?.orgId ?? '—'}
          </dd>
        </div>
      </dl>
    </TechnicalDetails>
  );
}

interface FieldErrors {
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

function useChangePasswordMutation(): UseMutationResult<
  ChangePasswordResponse,
  Error,
  ChangePasswordRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (body: ChangePasswordRequest) => apiChangePassword(body, fetchImpl),
  });
}

interface SetPasswordFieldErrors {
  code?: string;
  newPassword?: string;
  confirmPassword?: string;
}

function SetPasswordCard(): JSX.Element {
  const t = useT();
  const { fetchImpl, setUser } = useSession();
  const toast = useToast();
  const [expiresInSeconds, setExpiresInSeconds] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<SetPasswordFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const coolingDown = cooldownRemaining > 0;

  useEffect(() => {
    if (!coolingDown) {
      return;
    }
    const id = window.setInterval(() => {
      setCooldownRemaining((n) => (n <= 1 ? 0 : n - 1));
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [coolingDown]);

  async function refetchSession(): Promise<void> {
    const session = await fetchSession(fetchImpl);
    setUser(sessionUserFromResponse(session));
  }

  async function handleSend(): Promise<void> {
    setSendError(null);
    setFormError(null);
    setSending(true);
    try {
      const ack = await apiRequestSetPasswordCode(fetchImpl);
      setExpiresInSeconds(ack.expiresInSeconds);
      setCooldownRemaining(SEND_CODE_COOLDOWN_SECONDS);
    } catch (err) {
      if (err instanceof PlatformApiError && err.code === 'PASSWORD_ALREADY_SET') {
        await refetchSession();
        return;
      }
      setSendError(localizeAuthError(err, t, SET_PASSWORD_SEND_ERROR_KEYS));
    } finally {
      setSending(false);
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);
    setSendError(null);

    const nextErrors: SetPasswordFieldErrors = {};
    const trimmed = code.trim();
    if (!SET_PASSWORD_CODE_PATTERN.test(trimmed)) {
      nextErrors.code = t('settings.security.set_password.error.format');
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_short', {
        min: String(MIN_PASSWORD_LENGTH),
      });
    } else if (newPassword.length > MAX_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_long', {
        max: String(MAX_PASSWORD_LENGTH),
      });
    }
    if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = t('settings.security.error.mismatch');
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    setSubmitting(true);
    try {
      await apiSetPassword({ code: trimmed, newPassword }, fetchImpl);
      toast.success(t('settings.security.set_password.toast.success'));
      await refetchSession();
    } catch (err) {
      if (err instanceof PlatformApiError) {
        if (err.code === 'CODE_INVALID') {
          setFieldErrors({ code: t('settings.security.set_password.error.invalid') });
          return;
        }
        if (err.code === 'CODE_EXPIRED') {
          setFieldErrors({ code: t('settings.security.set_password.error.expired') });
          return;
        }
        if (err.code === 'CODE_RATE_LIMITED') {
          setFormError(t('settings.security.set_password.error.attempts_exhausted'));
          return;
        }
        if (err.code === 'PASSWORD_ALREADY_SET') {
          await refetchSession();
          return;
        }
        if (err.code === 'INVALID_PAYLOAD') {
          setFieldErrors({ newPassword: localizeApiError(err, t) });
          return;
        }
        setFormError(localizeApiError(err, t));
        return;
      }
      setFormError(t('settings.security.error.network'));
    } finally {
      setSubmitting(false);
    }
  }

  const sent = expiresInSeconds !== null;
  const ackMinutes = expiresInSeconds === null ? 0 : Math.max(1, Math.round(expiresInSeconds / 60));
  let sendLabel = t('settings.security.set_password.action');
  if (sending) {
    sendLabel = t('settings.security.set_password.sending');
  } else if (cooldownRemaining > 0) {
    sendLabel = t('settings.security.set_password.action_cooldown', {
      seconds: cooldownRemaining,
    });
  }

  return (
    <Panel caption={t('settings.security.title')} padded testId="settings-security-card">
      <div className="flex max-w-xl flex-col gap-4" data-testid="settings-set-password">
        <span className="text-label text-forest">
          {t('settings.security.set_password.heading')}
        </span>
        <p className="m-0 text-caption text-ink-low">{t('settings.security.set_password.body')}</p>
        <form
          className="flex flex-col gap-4"
          onSubmit={handleSubmit}
          data-testid="settings-set-password-form"
          noValidate
        >
          <FormField
            label={t('settings.security.set_password.code')}
            fieldId="settings-set-password-code"
            required
            error={
              fieldErrors.code !== undefined ? (
                <span data-testid="settings-set-password-code-error">{fieldErrors.code}</span>
              ) : undefined
            }
          >
            <div className="flex items-center gap-2">
              <Input
                id="settings-set-password-code"
                className="w-0 min-w-0 flex-1"
                mono
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                disabled={submitting || sending}
                required
                invalid={fieldErrors.code !== undefined}
                aria-describedby={
                  fieldErrors.code !== undefined ? 'settings-set-password-code-error' : undefined
                }
                data-testid="settings-set-password-code"
              />
              <Button
                type="button"
                variant={sent ? 'secondary' : 'primary'}
                loading={sending}
                disabled={submitting || cooldownRemaining > 0}
                className="shrink-0"
                onClick={() => {
                  void handleSend();
                }}
                data-testid="settings-set-password-send"
              >
                {sendLabel}
              </Button>
            </div>
            {sendError !== null ? (
              <p
                role="alert"
                className="m-0 text-label text-bad"
                data-testid="settings-set-password-send-error"
              >
                {sendError}
              </p>
            ) : null}
            {sent ? (
              <p
                className="m-0 text-caption text-ink-low"
                role="status"
                data-testid="settings-set-password-ack"
              >
                {t('settings.security.set_password.ack', { minutes: ackMinutes })}
              </p>
            ) : null}
          </FormField>
          <FormField
            label={t('settings.security.new_password')}
            fieldId="settings-set-password-new"
            required
            error={
              fieldErrors.newPassword !== undefined ? (
                <span data-testid="settings-set-password-new-error">{fieldErrors.newPassword}</span>
              ) : undefined
            }
          >
            <Input
              id="settings-set-password-new"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.currentTarget.value)}
              disabled={submitting || sending}
              required
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={MAX_PASSWORD_LENGTH}
              invalid={fieldErrors.newPassword !== undefined}
              aria-describedby={
                fieldErrors.newPassword !== undefined
                  ? 'settings-set-password-new-error'
                  : undefined
              }
              data-testid="settings-set-password-new"
            />
          </FormField>
          <FormField
            label={t('settings.security.confirm_password')}
            fieldId="settings-set-password-confirm"
            required
            error={
              fieldErrors.confirmPassword !== undefined ? (
                <span data-testid="settings-set-password-confirm-error">
                  {fieldErrors.confirmPassword}
                </span>
              ) : undefined
            }
          >
            <Input
              id="settings-set-password-confirm"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
              disabled={submitting || sending}
              required
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={MAX_PASSWORD_LENGTH}
              invalid={fieldErrors.confirmPassword !== undefined}
              aria-describedby={
                fieldErrors.confirmPassword !== undefined
                  ? 'settings-set-password-confirm-error'
                  : undefined
              }
              data-testid="settings-set-password-confirm"
            />
          </FormField>
          {formError !== null ? (
            <p
              role="alert"
              className="m-0 text-label text-bad"
              data-testid="settings-set-password-error"
            >
              {formError}
            </p>
          ) : null}
          <div className="flex items-center justify-end">
            <Button
              type="submit"
              variant="primary"
              loading={submitting}
              disabled={sending}
              data-testid="settings-set-password-submit"
            >
              {submitting
                ? t('settings.security.submitting')
                : t('settings.security.set_password.submit')}
            </Button>
          </div>
        </form>
      </div>
    </Panel>
  );
}

function ChangePasswordCard(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const mutation = useChangePasswordMutation();

  function resetForm(): void {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFieldErrors({});
    setFormError(null);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);
    setToast(null);

    const nextErrors: FieldErrors = {};
    if (currentPassword.length === 0) {
      nextErrors.currentPassword = t('settings.security.error.current_required');
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_short', {
        min: String(MIN_PASSWORD_LENGTH),
      });
    } else if (newPassword.length > MAX_PASSWORD_LENGTH) {
      nextErrors.newPassword = t('settings.security.error.password_too_long', {
        max: String(MAX_PASSWORD_LENGTH),
      });
    }
    if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = t('settings.security.error.mismatch');
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    try {
      await mutation.mutateAsync({ currentPassword, newPassword });
      setToast(t('settings.security.toast.success'));
      resetForm();
    } catch (err) {
      if (err instanceof PlatformApiError) {
        if (err.code === 'INVALID_CREDENTIALS') {
          setFieldErrors({
            currentPassword: t('settings.security.error.current_incorrect'),
          });
          return;
        }
        if (err.code === 'INVALID_PAYLOAD') {
          setFieldErrors({ newPassword: localizeApiError(err, t) });
          return;
        }
        setFormError(localizeApiError(err, t));
        return;
      }
      setFormError(t('settings.security.error.network'));
    }
  }

  const submitting = mutation.isPending;

  if (user?.hasPassword === false) {
    return <SetPasswordCard />;
  }

  return (
    <Panel caption={t('settings.security.title')} padded testId="settings-security-card">
      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={handleSubmit}
        data-testid="settings-change-password-form"
        noValidate
      >
        <span className="text-label text-forest">{t('settings.security.change_password')}</span>
        <FormField
          label={t('settings.security.current_password')}
          fieldId="settings-current-password"
          required
          error={
            fieldErrors.currentPassword !== undefined ? (
              <span data-testid="settings-current-password-error">
                {fieldErrors.currentPassword}
              </span>
            ) : undefined
          }
        >
          <Input
            id="settings-current-password"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.currentTarget.value)}
            disabled={submitting}
            required
            invalid={fieldErrors.currentPassword !== undefined}
            aria-describedby={
              fieldErrors.currentPassword !== undefined
                ? 'settings-current-password-error'
                : undefined
            }
            data-testid="settings-current-password"
          />
        </FormField>
        <FormField
          label={t('settings.security.new_password')}
          fieldId="settings-new-password"
          required
          error={
            fieldErrors.newPassword !== undefined ? (
              <span data-testid="settings-new-password-error">{fieldErrors.newPassword}</span>
            ) : undefined
          }
        >
          <Input
            id="settings-new-password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            disabled={submitting}
            required
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            invalid={fieldErrors.newPassword !== undefined}
            aria-describedby={
              fieldErrors.newPassword !== undefined ? 'settings-new-password-error' : undefined
            }
            data-testid="settings-new-password"
          />
        </FormField>
        <FormField
          label={t('settings.security.confirm_password')}
          fieldId="settings-confirm-password"
          required
          error={
            fieldErrors.confirmPassword !== undefined ? (
              <span data-testid="settings-confirm-password-error">
                {fieldErrors.confirmPassword}
              </span>
            ) : undefined
          }
        >
          <Input
            id="settings-confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            disabled={submitting}
            required
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={MAX_PASSWORD_LENGTH}
            invalid={fieldErrors.confirmPassword !== undefined}
            aria-describedby={
              fieldErrors.confirmPassword !== undefined
                ? 'settings-confirm-password-error'
                : undefined
            }
            data-testid="settings-confirm-password"
          />
        </FormField>
        {formError !== null ? (
          <p
            role="alert"
            className="m-0 text-label text-bad"
            data-testid="settings-change-password-error"
          >
            {formError}
          </p>
        ) : null}
        {toast !== null ? (
          <p
            role="status"
            className="m-0 rounded-lg border border-ok-line bg-ok-tint px-4 py-2 text-label text-ink"
            data-testid="settings-change-password-toast"
          >
            {toast}
          </p>
        ) : null}
        <div className="flex items-center justify-end">
          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            data-testid="settings-change-password-submit"
          >
            {submitting ? t('settings.security.submitting') : t('settings.security.submit')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
